import { useTranslation } from '@dreamstack/i18n'
import { Minus } from '@dreamstack/icons/src/icon-components/Minus'
import { Plus } from '@dreamstack/icons/src/icon-components/Plus'
import { cn } from '@dreamstack/util/src/cn'
import { CircularProgress } from '@mui/material'
import type { FunctionComponent } from 'react'
import React from 'react'
import { useGetAllValidProperties } from '../../hooks/useGetAllValidProperties'
import { useGetPropertiesFilteredAndSorted } from '../../hooks/useGetPropertiesFilteredAndSorted'
import { GridWithGaps } from '../../layout/MainGrid'
import { HeadingH1, HeadingH4 } from '../../layout/Texts'
import { usePropertySearchContext } from '../../next/PropertySearchContext'
import { PropertyFilter } from './PropertyFilter'
import { PropertyFilterSortMenu } from './PropertyFilterSortMenu'
import { PropertyFilterTextSummary } from './PropertyFilterTextSummary'

const PropertyIndexMobileFilterArea = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn('block py-px-24 md:hidden', className)} {...rest}>
    {children}
  </div>
)
const PropertyIndexMobileFilterAreaToggle = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{
  className?: string
}> &
  React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('block py-px-24 cursor-pointer md:hidden', className)}
    {...rest}
  >
    {children}
  </div>
)
const PropertyIndexMobileFilter = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'px-px-16 mt-px-24 w-full bg-accentroWhite-full md:hidden',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const PropertyIndexResultAreaInnerGrid = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <GridWithGaps
    className={cn('grid-cols-12 md:grid-cols-8 lg:grid-cols-9', className)}
    {...rest}
  >
    {children}
  </GridWithGaps>
)

const PropertyIndexFilterResultSubTitle = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'col-start-1 col-span-12 md:col-start-2 md:col-span-6 lg:col-start-2 lg:col-span-7',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const PropertyFilterOverviewResultHeading: FunctionComponent<
  React.PropsWithChildren<{
    mobileFilterOpen: boolean
    showNoResults: boolean
    setMobileFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  }>
> = ({ mobileFilterOpen, showNoResults, setMobileFilterOpen }) => {
  const { allPropertiesFilteredAndSorted, loading } =
    useGetPropertiesFilteredAndSorted()
  const { propertySearchFilterValues } = usePropertySearchContext()
  const t = useTranslation()
  const { allProperties } = useGetAllValidProperties()
  const filterSet =
    allProperties.length !== allPropertiesFilteredAndSorted.length

  return (
    <PropertyIndexResultAreaInnerGrid>
      <PropertyIndexFilterResultSubTitle>
        {(filterSet || loading) && !showNoResults && (
          <HeadingH1>
            {loading ? (
              <CircularProgress color="primary" />
            ) : (
              `${allPropertiesFilteredAndSorted.length} ${t(
                `accentro:rsTypes.${propertySearchFilterValues.typeOfImmo}`,
                {
                  count: allPropertiesFilteredAndSorted.length,
                }
              )}`
            )}
          </HeadingH1>
        )}
        <div className="flex flex-1 flex-col items-start md:justify-between lg:flex-row lg:items-center">
          {!showNoResults && (
            <HeadingH4>
              <PropertyFilterTextSummary />
            </HeadingH4>
          )}

          <PropertyIndexMobileFilter>
            <PropertyIndexMobileFilterAreaToggle
              onClick={() => setMobileFilterOpen((current) => !current)}
            >
              <div className="flex justify-between items-center">
                <HeadingH4>{t('accentro:filter-results')}</HeadingH4>
                {mobileFilterOpen ? <Minus /> : <Plus />}
              </div>
            </PropertyIndexMobileFilterAreaToggle>

            {mobileFilterOpen && (
              <PropertyIndexMobileFilterArea>
                <PropertyFilter />
              </PropertyIndexMobileFilterArea>
            )}
          </PropertyIndexMobileFilter>
          {!showNoResults && (
            <div className="mt-px-32 lg:mt-0 lg:text-right">
              <PropertyFilterSortMenu />
            </div>
          )}
        </div>
      </PropertyIndexFilterResultSubTitle>
    </PropertyIndexResultAreaInnerGrid>
  )
}
